import { Box } from '@mui/material';
import { useMemo } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { RequireReduxData } from '~/components/RequireReduxData';
import { CustomTranslationList } from '~/scenes/AdminModule/components/People/components/ValueLists/CustomTranslations';
import { WorkflowsConfigurationView } from '~/scenes/AdminModule/components/Views/WorkflowsConfigurationView';
import { useAppModules } from '~/scenes/AppModules/useAppModules';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';
import {
  NavigationRoutingStructure,
  useStructuredModuleRouting,
} from '~/scenes/PortalViews/components/StructuredModuleRouter';
import { HomePageSettings } from '~/scenes/PortalViews/Views/AdminViews/Common/HomePageSettings';
import { PortalBrandingSettings } from '~/scenes/PortalViews/Views/AdminViews/Common/PortalBrandingSettings';
import { requiredReduxData } from '~/scenes/PortalViews/Views/AdminViews/data';
import { AppModules, isRoleHRAdmin } from '~/services/Permissions';

export const useAdminViews = () => {
  const { t } = useTranslation();
  const userRoles = useLoggedInUserRoles();
  const userHasAccess = isRoleHRAdmin(userRoles);

  const { isModuleActive } = useAppModules();

  const initialNavigationStructure: NavigationRoutingStructure[] =
    useMemo(() => {
      const renderLoading = (percentage: string) => (
        <LoadingView>
          {t('loadingPortalView')} - {percentage}
        </LoadingView>
      );

      return [
        {
          path: 'people',
          label: t('laPeople'),
          subPaths: [
            {
              path: 'custom-translations',
              label: t('customTranslations'),
              component: (
                <RequireReduxData
                  required={requiredReduxData.CustomTranslationList}
                  renderLoading={renderLoading}
                >
                  <CustomTranslationList />
                </RequireReduxData>
              ),
            },
          ],
        },
        {
          path: 'common',
          label: t('laCommon'),
          subPaths: [
            {
              path: 'homepage-settings',
              label: t('homePageSettings'),
              component: (
                <RequireReduxData
                  required={requiredReduxData.HomePageSettings}
                  renderLoading={renderLoading}
                >
                  <HomePageSettings />
                </RequireReduxData>
              ),
            },
            {
              path: 'branding-settings',
              label: t('portalBrandingSetting'),
              isAvailable: isModuleActive(AppModules.BRANDING),
              component: (
                <RequireReduxData
                  required={requiredReduxData.PortalBrandingSettings}
                  renderLoading={renderLoading}
                >
                  <PortalBrandingSettings />
                </RequireReduxData>
              ),
            },
          ],
        },
        {
          path: 'workflow',
          label: t('laWorkFlow'),
          subPaths: [
            {
              path: 'workflow',
              label: t('laWorkFlow'),
              isLegacy: true,
              component: <Box>Old Workflows</Box>,
            },
            {
              path: 'workflow2',
              label: `${t('laWorkFlow')} 2.0`,
              component: (
                <RequireReduxData
                  required={requiredReduxData.WorkflowsConfigurationView}
                  renderLoading={renderLoading}
                >
                  <WorkflowsConfigurationView />
                </RequireReduxData>
              ),
            },
          ],
        },
      ];
    }, [t, isModuleActive]);

  const processedRoutingProps = useStructuredModuleRouting(
    initialNavigationStructure,
  );

  return {
    userHasAccess,
    ...processedRoutingProps,
  };
};
